import React, { useState } from "react";
import api from "../api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Flag } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../auth";

const REASONS_USER = [
  { value: "fraud", label: "Fraude / faux profil" },
  { value: "inappropriate", label: "Comportement inapproprié" },
  { value: "safety", label: "Risque pour la sécurité" },
  { value: "qualification", label: "Diplôme / qualification douteux(se)" },
  { value: "other", label: "Autre" },
];

const REASONS_POOL = [
  { value: "wrong_info", label: "Informations incorrectes" },
  { value: "closed", label: "Établissement fermé / inexistant" },
  { value: "safety", label: "Conditions de sécurité / hygiène" },
  { value: "duplicate", label: "Doublon" },
  { value: "other", label: "Autre" },
];

/**
 * Reusable report dialog. Pass:
 *  - targetType: "user" | "pool"
 *  - targetId: string
 *  - targetLabel: string (display)
 *  - trigger: optional React node; if omitted, a default Flag button is rendered
 *  - onBlocked: callback fired after a successful block (only for users)
 */
export default function ReportDialog({ targetType, targetId, targetLabel, trigger, onBlocked, size = "sm" }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [custom, setCustom] = useState("");
  const [blockToo, setBlockToo] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const reasons = targetType === "user" ? REASONS_USER : REASONS_POOL;
  const isSelf = user && targetType === "user" && targetId === user.user_id;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!user) {
      toast.error("Connectez-vous pour signaler.");
      return;
    }
    if (!reason) return toast.error("Sélectionnez un motif");
    if (reason === "other" && !custom.trim()) return toast.error("Précisez le motif dans le message");
    setSubmitting(true);
    try {
      const { data } = await api.post("/reports", {
        target_type: targetType,
        target_id: targetId,
        reason,
        custom_text: custom,
        block: targetType === "user" && blockToo && !isSelf,
      });
      toast.success(
        data.blocked
          ? "Signalement envoyé. Utilisateur bloqué — vous pouvez continuer votre navigation."
          : "Signalement envoyé. Sous 48h sans réponse, vérifiez vos courriers indésirables.",
        { duration: 6000 }
      );
      setOpen(false);
      setReason(""); setCustom(""); setBlockToo(false);
      if (data.blocked && onBlocked) onBlocked();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setSubmitting(false); }
  };

  const defaultTrigger = (
    <Button
      type="button"
      variant="ghost"
      size={size}
      onClick={() => setOpen(true)}
      className="rounded-full text-red-700 hover:bg-red-50"
      data-testid={`report-btn-${targetType}-${targetId}`}
    >
      <Flag className="w-3.5 h-3.5 mr-1" />Signaler
    </Button>
  );

  return (
    <>
      {trigger
        ? React.cloneElement(trigger, { onClick: () => setOpen(true) })
        : defaultTrigger}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Flag className="w-5 h-5" />Signaler {targetType === "user" ? "cet utilisateur" : "cet établissement"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="report-form">
            <p className="text-sm text-slate-600">
              <span className="font-semibold text-slate-900">{targetLabel}</span><br />
              Votre signalement sera transmis à l'équipe de modération de MySwimmingLessons.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-[11px] text-amber-900" data-testid="report-spam-notice">
              ℹ️ Une réponse vous parviendra par email sous 48h. <strong>Pensez à vérifier vos courriers indésirables / spam</strong> en l'absence de réponse.
            </div>
            <div>
              <Label className="text-sm">Motif <span className="text-red-500">*</span></Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1">
                {reasons.map((r) => (
                  <label key={r.value} className={`border rounded-xl p-2 text-sm cursor-pointer transition ${reason === r.value ? "border-red-500 bg-red-50 text-red-700" : "border-slate-200 hover:border-red-300"}`}>
                    <input type="radio" name="report-reason" className="sr-only" checked={reason === r.value}
                      onChange={() => setReason(r.value)} data-testid={`report-reason-${r.value}`} />
                    {r.label}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-sm">Précisions {reason === "other" && <span className="text-red-500">*</span>}</Label>
              <Textarea
                rows={4}
                value={custom}
                onChange={(e) => setCustom(e.target.value)}
                placeholder="Décrivez ce que vous avez constaté (faits, dates, captures…)"
                data-testid="report-custom-text"
              />
            </div>
            {targetType === "user" && !isSelf && (
              <label className="flex items-start gap-2 bg-slate-50 p-3 rounded-xl text-sm cursor-pointer">
                <input type="checkbox" checked={blockToo} onChange={(e) => setBlockToo(e.target.checked)} className="mt-0.5" data-testid="report-block-toggle" />
                <span>
                  <strong>Bloquer également cet utilisateur</strong>
                  <span className="block text-xs text-slate-500 mt-0.5">Vous ne verrez plus son contenu et il ne pourra plus vous contacter via la plateforme. Vous pouvez débloquer à tout moment depuis votre profil.</span>
                </span>
              </label>
            )}
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" className="rounded-full" onClick={() => setOpen(false)} disabled={submitting}>Annuler</Button>
              <Button type="submit" className="rounded-full bg-red-600 hover:bg-red-500 text-white" disabled={submitting} data-testid="report-submit-btn">
                {submitting ? "Envoi…" : "Envoyer le signalement"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
